import { MarketActivityIndicator, MarketDialog } from '@market/react';
import { useEffect, useRef, useState } from 'react';
import { useLocationIdToFulfillmentConfigurationMap } from '../../hooks/useFulfillmentConfiguration.js';
import { useActiveLocations } from '../../hooks/useLocations.js';
import { PrepTimeLocationModalContent } from '../prep-time-location-modal-content/prep-time-location-modal-content.js';
import { PrepTimeSelectionModalContent } from '../prep-time-selection-modal-content/prep-time-selection-modal-content.js';
import styles from './prep-time-modal.module.css';

type ModalState = 'loading' | 'location-selection' | 'prep-time-selection';

export interface PrepTimeModalProps {
  readonly onClose: () => void;
  readonly onSave: (success: boolean) => void;
}

export function PrepTimeModal({
  onClose,
  onSave,
}: PrepTimeModalProps) {
  const { data: activeLocations, isPending: isLoadingLocations } = useActiveLocations();
  const locationIds = activeLocations?.locations
    ?.map((location) => location.id)
    .filter((id): id is string => id !== undefined) || [];
  const locationIdToConfigMap = useLocationIdToFulfillmentConfigurationMap(locationIds);
  const isMultiLocation = (activeLocations?.locations?.length || 0) > 1;
  const [modalState, setModalState] = useState<ModalState>('loading');
  const [currentLocationId, setCurrentLocationId] = useState<string | undefined>();
  const hasInitialized = useRef(false);

  // Determine if we should show single or multi-location flow once locations are ready
  useEffect(() => {
    if (isLoadingLocations ||
      activeLocations?.locations?.length === 0 ||
      Object.keys(locationIdToConfigMap).length === 0 ||
      hasInitialized.current
    ) {
      return;
    }

    // Prevents infinite render loop
    hasInitialized.current = true;

    if (isMultiLocation) {
      setCurrentLocationId(undefined);
      setModalState('location-selection');
    } else {
      // For single location, use the first location
      setCurrentLocationId(activeLocations?.locations?.[0]?.id);
      setModalState('prep-time-selection');
    }
  }, [isLoadingLocations, isMultiLocation, activeLocations?.locations, locationIdToConfigMap]);

  const currentLocation = activeLocations?.locations?.find(
    ({ id }) => id === currentLocationId,
  );
  const currentFulfillmentConfiguration = currentLocationId
    ? locationIdToConfigMap[currentLocationId]
    : undefined;

  const handleLocationSelect = (locationId: string) => {
    setCurrentLocationId(locationId);
    setModalState('prep-time-selection');
  };

  const handlePrepTimeModalBack = () => {
    if (isMultiLocation) {
      setModalState('location-selection');
      setCurrentLocationId(undefined);
    } else {
      onClose();
    }
  };

  const body = () => {
    if (modalState === 'loading') {
      return (
        <div className={styles.loadingContainer}>
          <MarketActivityIndicator />
        </div>
      );
    }

    if (modalState === 'location-selection') {
      return (
        <PrepTimeLocationModalContent
          locations={activeLocations?.locations || []}
          locationIdToFulfillmentConfigMap={locationIdToConfigMap}
          onClose={onClose}
          onLocationSelect={handleLocationSelect}
        />
      );
    }

    if (modalState === 'prep-time-selection') {
      return (
        <PrepTimeSelectionModalContent
          currentFulfillmentConfiguration={currentFulfillmentConfiguration}
          currentLocation={currentLocation}
          onBack={isMultiLocation ? handlePrepTimeModalBack : undefined}
          onClose={onClose}
          onSave={onSave}
        />
      );
    }

    return null;
  };

  return (
    <MarketDialog
      data-testid="prep-time-modal"
      onMarketDialogDismissed={onClose}
    >
      {body()}
    </MarketDialog>
  );
}
